import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { BreadcrumbsComponent } from '../breadcrumbs/breadcrumbs.component';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { SearchService } from '../../services/search.service';
import { filter } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';

interface BreadcrumbItem {
  label: string;
  url?: string;
}

@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [CommonModule, RouterModule, BreadcrumbsComponent, SearchBarComponent, DropdownComponent],
  templateUrl: './page-header.component.html',
  styleUrl: './page-header.component.scss',
})
export class PageHeaderComponent {
  currentPage: string = '';
  searchPlaceholder: string = 'Search title or description';
  currentModule: string = '';
  resetSearch = false;
  resetModule = false;
  breadcrumbs: BreadcrumbItem[] = [];
  showFaq: boolean = false;
  modules: any[] = [];
  currentProjectId = '';
  currentSortOrder  = 'desc';

  // ✅ Tab definitions
  tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'videos', label: 'Videos' },
    { id: 'documents', label: 'Documents' },
    { id: 'faq', label: 'FAQ' },
    // { id: 'discussions', label: 'Discussions' },
    // { id: 'updates', label: 'Updates' },
  ];

  constructor(private route: ActivatedRoute, public router: Router, private searchService: SearchService, private api: ApiService) {
    // Extract current module from route params
    // this.route.params.subscribe((params) => {
    //   this.currentModule = params['module'] || 'all';
    // });

    this.router.events
    .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
    .subscribe((event) => {
      this.updateBreadcrumbs();
      this.triggerResetStates();
    });

  }

  ngOnInit() {
    // Get projectId dynamically based on route
    this.route.paramMap.subscribe(params => {
      const moduleSlug = params.get('module');
      if (moduleSlug) this.loadProjectAndModules(moduleSlug);
    });
  }


  updateBreadcrumbs() {
    const url = this.router.url;
    const parts = url.split('/').filter(x => x);
    this.breadcrumbs = [];
    this.currentPage = '';

    if (parts[0] === 'content') {
      const module = parts[1];
      const page = parts[2];
      const id = parts[3];

      // 1️⃣ Module breadcrumb — only a label, no route
      this.breadcrumbs.push({
        label: this.formatModuleName(module),
        url: undefined, // Not clickable
      });

      // 2️⃣ Overview page
      if (page === 'overview' || !page) {
        this.breadcrumbs.push({
          label: 'Overview',
          url: undefined, // Not clickable
        });
        this.currentPage = '';
      }

      // 3️⃣ Top-level tab pages (videos, documents, discussions, updates)
      else if (['videos', 'documents', 'discussions', 'updates'].includes(page)) {
        this.breadcrumbs.push({
          label: this.capitalize(page),
          url: `/content/${module}/${page}`,
        });
        this.currentPage = '';
      }

      // 4️⃣ Detail pages (video/:id or document/:id)
      else if ((page === 'video' || page === 'document') && id) {
        this.breadcrumbs.push({
          label: this.capitalize(page + 's'),
          url: `/content/${module}/${page}s`, // ✅ This ensures it's clickable
        });
        this.currentPage = 'Detail';
      }
    }

    console.log('Breadcrumbs:', this.breadcrumbs, 'Current Page:', this.currentPage);
  }

  loadProjectAndModules(moduleSlug: string) {
    this.api.get<any[]>('/projects').subscribe({
      next: (projects) => {
        const matched = projects.find(p => this.slugify(p.name) === moduleSlug);
        if (matched) {
          this.currentProjectId = matched._id;
          // Fetch modules for this project
          this.api.get<any[]>(`/projects/${matched._id}/modules`).subscribe({
            next: (res) => {
              this.modules = [
                { name: 'All Modules', value: 'all' },
                ...res.map(m => ({ name: m.moduleName, value: m._id }))
              ];
              this.currentModule = 'all';
            },
            error: (err) => console.error('Failed to load project modules', err),
          });
        }
      },
      error: (err) => console.error('Failed to fetch projects', err),
    });
  }

  slugify(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-');
  }

  formatModuleName(name: string): string {
    return name.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  capitalize(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  isDetailPage(): boolean {
  const url = this.router.url;

  // Match: /content/<any-module-name>/(video|document)/<mongoId>
  const regex = /^\/content\/[^\/]+\/(video|document)\/[a-f0-9]+$/;

  return regex.test(url);
}

  toggleFaq() {
    this.showFaq = !this.showFaq;
  }

  triggerResetStates() {
    // reset search input
    this.resetSearch = true;
    setTimeout(() => (this.resetSearch = false), 0);

    this.currentModule = 'all';
    // reset module dropdown (emit 'all' to video component)
    this.searchService.emitModuleFilter('all');

    this.currentSortOrder = 'desc';
    this.searchService.emitSortOrder('desc');
  }

  /** Handle dropdown filter changes */
  onSearch(value: string) {
    console.log('Search:', value);
    this.searchService.emitSearch(value);
  }

  onSortChange(value: string) {
    this.currentSortOrder = value as 'asc' | 'desc';
    this.searchService.emitSortOrder(this.currentSortOrder);
  }

  onModuleChange(value: string) {
    this.currentModule = value;
    this.searchService.emitModuleFilter(value); 
  }

  onCategoryChange(value: string) {
    console.log('Category:', value);
  }
}
